/**
 * Sub-component (not a routed page): one row in an admin list. Exports the ListItem
 * union (bounties, submissions, payments, users, training data sets, RAG entries)
 * and the ListItemType discriminator, then narrows the item by type to show title,
 * id, thumbnail image, product link, created/updated dates, and status.
 * Rendered by components/PaginatedList inside AdminListViewContainer; makes no API
 * calls itself.
 */
import React from "react";
import { AiBounty } from "@/types/aiBounty";
import { Bounty } from "@/types/bounty";
import { AiSubmission } from "@/types/aiSubmission";
import { StripePayment } from "@/types/payment";
import { SubmissionDisplay } from "@/types/submission";
import { Link as LinkIcon, CheckCircle, XCircle } from "lucide-react";
import { PrivateUser } from "@/types/user";
import { formatDateToLocal } from "@/utils/datetime";
import {
    SearchTrainingDataSetDisplay,
    SubmissionVerificationTrainingDataSetDisplay,
} from "@/types/trainingData";
import { RagDomainEntry } from "@/types/aiRag";

export type ListItem =
    | AiBounty
    | AiSubmission
    | StripePayment
    | SubmissionDisplay
    | Bounty
    | PrivateUser
    | SubmissionVerificationTrainingDataSetDisplay
    | SearchTrainingDataSetDisplay
    | RagDomainEntry;

export type ListItemType =
    | "ai_bounty"
    | "bounty"
    | "submission"
    | "payment"
    | "ai_submission"
    | "user"
    | "submission_verification_training_data_set"
    | "search_training_data_set";

interface AdminListItemProps {
    item: ListItem;
    type: ListItemType;
    onClick?: () => void;
}

export const AdminListItem: React.FC<AdminListItemProps> = ({ item, type, onClick }) => {
    const getTitleText = (): string => {
        if (type === "ai_bounty") {
            const aiBounty = item as AiBounty;
            return aiBounty.title;
        } else if (type === "bounty") {
            const bounty = item as Bounty;
            return bounty.title;
        } else if (type === "submission") {
            const submission = item as SubmissionDisplay;
            return submission.title;
        } else if (type === "ai_submission") {
            const aiSubmission = item as AiSubmission;
            return aiSubmission.title;
        } else if (type === "payment") {
            const payment = item as StripePayment;
            return `$${payment.chargeAmount}`;
        } else if (type === "user") {
            const user = item as PrivateUser;
            return user.username;
        } else if (type === "submission_verification_training_data_set") {
            const trainingDataSet = item as SubmissionVerificationTrainingDataSetDisplay;
            return trainingDataSet.label;
        }
        return "";
    };

    // Helper to get item id based on item type
    const getItemId = (item: ListItem): number | string | null => {
        if ("id" in item && item.id !== undefined) {
            return item.id;
        }
        return null;
    };

    const getCreatedAt = (item: ListItem): string | null => {
        if ("createdAt" in item && item.createdAt !== undefined) {
            return formatDateToLocal(item.createdAt);
        }
        return null;
    };

    const getUpdatedAt = (item: ListItem): string | null => {
        if ("updatedAt" in item && item.updatedAt !== undefined) {
            return formatDateToLocal(item.updatedAt);
        }
        return null;
    };

    const getStatusText = (item: ListItem): string | null => {
        if ("status" in item && item.status !== undefined) {
            return item.status;
        }
        return null;
    };

    const getImage = (item: ListItem): string | null => {
        if (
            "s3ImageUrls" in item &&
            Array.isArray(item.s3ImageUrls) &&
            item.s3ImageUrls.length > 0
        ) {
            return item.s3ImageUrls[0];
        } else if (
            "imageUrls" in item &&
            Array.isArray(item.imageUrls) &&
            item.imageUrls.length > 0
        ) {
            return item.imageUrls[0];
        } else if ("profilePictureUrl" in item && item.profilePictureUrl) {
            return item.profilePictureUrl;
        } else if (
            "extractedData" in item &&
            item.extractedData &&
            "imageUrls" in item.extractedData &&
            item.extractedData.imageUrls &&
            item.extractedData.imageUrls.length > 0
        ) {
            return item.extractedData.imageUrls[0];
        } else if ("extractedData" in item && item.extractedData) {
            if (
                "results" in item.extractedData &&
                item.extractedData.results &&
                Array.isArray(item.extractedData.results) &&
                item.extractedData.results.length > 0
            ) {
                return item.extractedData.results[0].imageUrl;
            }
        }
        return null;
    };

    const getProductUrl = (item: ListItem): string | null => {
        if ("productUrl" in item && item.productUrl) {
            return item.productUrl;
        } else if ("extractedData" in item && item.extractedData) {
            if ("productUrl" in item.extractedData && item.extractedData.productUrl) {
                return item.extractedData.productUrl;
            } else if (
                "results" in item.extractedData &&
                item.extractedData.results &&
                Array.isArray(item.extractedData.results) &&
                item.extractedData.results.length > 0
            ) {
                return item.extractedData.results[0].productUrl;
            }
        }
        return null;
    };

    return (
        <>
            <div
                className={`p-2 border-b hover:bg-gray-50 cursor-pointer transition-colors flex items-center h-24`}
                onClick={onClick}
            >
                <div className="flex-1 flex items-center justify-between gap-2">
                    {/* Primary Info - Always Visible */}
                    <div className="flex items-center gap-2">
                        {getItemId(item) && (
                            <div className="w-4 h-4 text-black font-porsui flex items-center justify-center mr-2">
                                <p className="text-xs md:text-sm">{getItemId(item)}</p>
                            </div>
                        )}
                        <div className="flex flex-row items-center gap-2 md:gap-4">
                            {getImage(item) ? (
                                <div className="flex-shrink-0">
                                    <img
                                        src={getImage(item) ?? ""}
                                        alt="item"
                                        className="w-12 h-12 rounded-xl"
                                    />
                                </div>
                            ) : (
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 rounded-xl bg-black" />
                                </div>
                            )}
                            {getProductUrl(item) && (
                                <LinkIcon
                                    className="flex-shrink-0 w-4 h-4"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(getProductUrl(item)!, "_blank");
                                    }}
                                />
                            )}
                            {/* Title */}
                            <div
                                className={`flex flex-col flex-shrink-0 overflow-hidden ${
                                    type === "submission_verification_training_data_set"
                                        ? "min-w-[256px] max-w-[256px]"
                                        : "w-48"
                                }`}
                            >
                                <h3 className="flex-shrink-0 font-semibold text-sm md:text-lg line-clamp-2">
                                    {getTitleText()}
                                </h3>
                            </div>
                        </div>
                    </div>

                    {/* Secondary Info - Hidden on Mobile */}
                    <div className="hidden md:flex flex-row items-center gap-4">
                        {/* Stripe status */}
                        {type === "user" && (
                            <div className="flex flex-col">
                                {(item as PrivateUser).stripeOnboardingCompleted ? (
                                    <CheckCircle className="w-4 h-4 text-black" />
                                ) : (
                                    <XCircle className="w-4 h-4 text-black" />
                                )}
                            </div>
                        )}
                        {getCreatedAt(item) && (
                            <div className="flex flex-col min-w-[80px]">
                                <p className="text-black text-xs font-bold">created: </p>
                                <p className="text-gray-600 text-xs">{getCreatedAt(item)}</p>
                            </div>
                        )}
                        {getUpdatedAt(item) && (
                            <div className="flex flex-col min-w-[80px]">
                                <p className="text-black text-xs font-bold">updated: </p>
                                <p className="text-gray-600 text-xs">{getUpdatedAt(item)}</p>
                            </div>
                        )}
                        {getStatusText(item) && (
                            <div className="flex flex-col w-[80px]">
                                <p className="text-black text-sm md:text-md font-bold text-right">
                                    {getStatusText(item)}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
